import React, { useState } from 'react'
import { Show, DateField } from '@refinedev/antd'
import { useShow, useInvalidate } from '@refinedev/core'
import { App, Button, Card, Col, Input, Modal, Row, Space } from 'antd'
import { brand } from '../../theme/brand'
import { Pill, type Tone } from '../../theme/listkit'
import { supabaseClient } from '../../lib/supabaseClient'
import { fnErrorMessage } from '../../lib/fnError'
import {
  allowedActions,
  statusTone,
  statusLabel,
  successMessage,
  typeLabel,
  serviceAreaLabel,
  type BusinessStatus,
  type DecisionAction,
} from './decision'
import { SELECT as LIST_SELECT, type Business } from './list'
import { VouchesCard } from './VouchesCard'
import { useJaguarOwnedSwitches } from '../jaguar_owned_controls/useJaguarOwnedSwitches'

interface BusinessWithPerks extends Business {
  perks?: { title: string; details: string | null; code: string | null; active: boolean }[]
}

// Builds on the list's SELECT rather than repeating the owner-embed string —
// one select string, one place to change it.
const SELECT = `${LIST_SELECT}, perks:business_perks(title,details,code,active)`

const headStyle: React.CSSProperties = {
  fontFamily: brand.fDisplay,
  fontWeight: 700,
  color: brand.ink,
}

const labelStyle: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: brand.gold,
  marginBottom: 4,
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13.5, color: brand.ink }}>{children}</div>
    </div>
  )
}

const ACTION_BUTTON_LABEL: Record<DecisionAction, string> = {
  approve: 'Approve & publish',
  request_changes: 'Request changes',
  reject: 'Reject',
  remove: 'Remove listing',
}

/** The note modal's title per action (approve never opens this modal). */
const NOTE_MODAL_TITLE: Record<Exclude<DecisionAction, 'approve'>, string> = {
  request_changes: 'Request changes to this listing?',
  reject: 'Reject this listing?',
  remove: 'Remove this listing?',
}

/** The note modal's OK button label per action — distinct from the generic 'OK'. */
const NOTE_MODAL_OK_TEXT: Record<Exclude<DecisionAction, 'approve'>, string> = {
  request_changes: 'Send back for changes',
  reject: 'Reject listing',
  remove: 'Remove listing',
}

/** Photo-moderation pill tone, mirroring the list's Checks column. */
function moderationTone(status: string): Tone {
  if (status === 'approved') return 'green'
  if (status === 'pending') return 'neutral'
  return 'red' // flagged or rejected
}

function moderationLabel(status: string): string {
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : '—'
}

/**
 * Jaguar-Owned listing — detail + decision surface.
 *
 * There is no admin UPDATE policy on `businesses` (§ the migration's own
 * comment): every decision — approve, request changes, reject, remove — goes
 * through the business-listing-decide Edge Function, which applies the change
 * via decide_business_listing(), notifies the owner, and writes the audit
 * trail. A stray useUpdate here would either fail outright or silently skip
 * that cascade, so this page never calls one.
 *
 * `noteModal` holds which non-approve action is mid-flow (or null); all three
 * share ONE modal + textarea (data-testid="business-decision-note") since they
 * differ only in title and the action sent to `decide`. Approve alone skips
 * the note and goes through Modal.confirm, per the brief.
 */
export function BusinessShow() {
  const { query } = useShow<BusinessWithPerks>({ meta: { select: SELECT } })
  const { data, isLoading } = query
  const record = data?.data

  // modal.confirm from the App context (not the static Modal.confirm) — the
  // static function can't consume context under this app's React 19 + antd v5
  // setup and silently renders nothing (see the AppShell comment in App.tsx
  // about static notification/message falling back the same way).
  const { message, modal } = App.useApp()
  const invalidate = useInvalidate()

  // Perks switch (Task C4): the listing page keeps showing the perks card
  // either way — admins keep auditing what's on file while perks are off —
  // but adds a note so an admin doesn't mistake a live-looking perk for one
  // members can actually see.
  //
  // A missing row and an explicit off both leave `perksOn` false, but they're
  // NOT the same thing on the server, and the note distinguishes them
  // (M5, fix round 3):
  //   * The MEMBER READ policy (`"business_perks: read with listing"`,
  //     `20260923121200_jaguar_owned_feature_switches.sql`) goes through
  //     `jaguar_owned_flag()`, whose `coalesce(..., false)` treats a missing
  //     row exactly like an explicit off — a member genuinely sees no perks
  //     either way. That clause has only existed since `121200`; before it
  //     landed, the read policy had no perks clause at all, and perks were
  //     always visible regardless of any row.
  //   * The Eligibility WAIVER (`jaguar_owned_perks_waived()`) is the
  //     opposite on purpose: it reads the raw row, and a MISSING row is NOT
  //     waived — the Business of the Month perk requirement keeps applying
  //     until an admin explicitly turns perks off (see the migration's own
  //     "THE WAIVER" comment for why).
  // In short: a missing row behaves like "off" for what members can see, but
  // like "on" for what the perk requirement still demands.
  const { byKey: switches, isLoading: switchesLoading, isError: switchesError } = useJaguarOwnedSwitches()
  const switchesLoaded = !switchesLoading && !switchesError
  const perksRow = switches.get('jaguar_owned_perks_enabled')
  const perksOn = perksRow?.enabled ?? false
  const perksRowMissing = perksRow === undefined

  // Vouches switch (Task P2C) — same shape as perks above. The vouches card
  // below (VouchesCard) keeps showing every vouch on file either way —
  // admins keep moderating vouches while the switch is off — this note just
  // tells an admin that members themselves currently see none of them.
  const vouchesRow = switches.get('jaguar_owned_vouches_enabled')
  const vouchesOn = vouchesRow?.enabled ?? false
  const vouchesRowMissing = vouchesRow === undefined

  // Which single action is in flight, or null. Only that action's button shows
  // a spinner; every button is disabled while ANY action is running (there is
  // exactly one decision to make at a time on a given listing).
  const [busy, setBusy] = useState<DecisionAction | null>(null)
  const [noteModal, setNoteModal] = useState<Exclude<DecisionAction, 'approve'> | null>(null)
  const [note, setNote] = useState('')

  async function decide(action: DecisionAction, noteText?: string) {
    if (!record) return
    setBusy(action)
    try {
      const { data: result, error } = await supabaseClient.functions.invoke('business-listing-decide', {
        body: { businessId: record.id, action, note: noteText || undefined },
      })
      if (error) throw error
      const payload = (result ?? {}) as { error?: string; status?: string }
      if (payload.error) throw new Error(payload.error)

      message.success(successMessage(action, record.name))
      setNoteModal(null)
      setNote('')
      invalidate({ resource: 'businesses', invalidates: ['list', 'detail'], id: record.id })
      query.refetch()
    } catch (e) {
      message.error(await fnErrorMessage(e))
    } finally {
      setBusy(null)
    }
  }

  function confirmApprove() {
    if (!record) return
    modal.confirm({
      title: 'Approve this listing?',
      content: `${record.name} goes live in Jaguar-Owned and the owner is notified in the app.`,
      okText: 'Yes, approve',
      cancelText: 'Cancel',
      onOk: () => decide('approve'),
    })
  }

  function openNoteModal(action: Exclude<DecisionAction, 'approve'>) {
    setNote('')
    setNoteModal(action)
  }

  function closeNoteModal() {
    setNoteModal(null)
    setNote('')
  }

  const actions = record ? allowedActions(record.status as BusinessStatus) : []
  const trimmedNote = note.trim()

  return (
    <Show isLoading={isLoading} title={false} breadcrumb={false} headerButtons={() => null}>
      <div style={{ maxWidth: 1200 }}>
        {/* ── Hero ─────────────────────────────────────────────────── */}
        <div
          style={{
            background: `linear-gradient(135deg, ${brand.navy} 0%, ${brand.jaguar} 100%)`,
            borderRadius: 14,
            padding: '22px 24px',
            marginBottom: 18,
          }}
        >
          <div
            style={{
              fontFamily: brand.fSans,
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: '.09em',
              textTransform: 'uppercase',
              color: brand.gold,
              marginBottom: 6,
            }}
          >
            Jaguar-Owned listing
          </div>
          <div style={{ fontFamily: brand.fDisplay, fontWeight: 700, fontSize: 24, color: '#fff' }}>
            {record?.name ?? '—'}
          </div>
          <Space size={10} style={{ marginTop: 10 }} wrap align="center">
            {record && <Pill tone={statusTone(record.status)}>{statusLabel(record.status)}</Pill>}
            <span style={{ fontFamily: brand.fSans, fontSize: 12.5, color: 'rgba(255,255,255,.75)' }}>
              {record ? (
                <>
                  Submitted{' '}
                  <DateField
                    value={record.submitted_at}
                    format="MMM D, YYYY"
                    style={{ color: 'inherit', fontFamily: 'inherit', fontSize: 'inherit' }}
                  />{' '}
                  by{' '}
                  {record.owner?.name ?? 'Unknown owner'} (Class of {record.owner?.class_year ?? '—'})
                </>
              ) : null}
            </span>
          </Space>
        </div>

        <Row gutter={16}>
          {/* ── Left: Listing ──────────────────────────────────────── */}
          <Col xs={24} lg={14}>
            <Card title={<span style={headStyle}>Listing</span>} style={{ marginBottom: 16 }}>
              {record?.cover_url ? (
                <img
                  src={record.cover_url}
                  alt={`${record.name} cover`}
                  style={{ maxHeight: 220, width: '100%', objectFit: 'cover', borderRadius: 10, marginBottom: 14 }}
                />
              ) : null}
              {record?.photo_urls && record.photo_urls.length > 0 ? (
                <Space size={8} wrap style={{ marginBottom: 14 }}>
                  {record.photo_urls.map((url) => (
                    <img
                      key={url}
                      src={url}
                      alt=""
                      style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8 }}
                    />
                  ))}
                </Space>
              ) : null}

              <Field label="Tagline">{record?.tagline || <span style={{ color: brand.sub }}>—</span>}</Field>
              <Field label="Description">
                {record?.description || <span style={{ color: brand.sub }}>—</span>}
              </Field>
              <Field label="Category">{record?.category_slug ?? '—'}</Field>
              <Field label="Type">{record ? typeLabel(record.business_type) : '—'}</Field>
              {record?.business_type === 'storefront' ? (
                <Field label="Address">
                  {[record.address, record.city, record.state].filter(Boolean).join(', ') || (
                    <span style={{ color: brand.sub }}>—</span>
                  )}
                </Field>
              ) : null}
              {record?.business_type === 'service_area' ? (
                <>
                  <Field label="Service areas">
                    {record.service_areas.length > 0
                      ? record.service_areas.map(serviceAreaLabel).join(', ')
                      : '—'}
                  </Field>
                  {record.service_area_note ? (
                    <Field label="Service area note">{record.service_area_note}</Field>
                  ) : null}
                </>
              ) : null}
              <Field label="Hours">{record?.hours || <span style={{ color: brand.sub }}>—</span>}</Field>
              <Field label="Phone">{record?.phone_e164 || <span style={{ color: brand.sub }}>—</span>}</Field>
              <Field label="WhatsApp">
                {record?.whatsapp_e164 || <span style={{ color: brand.sub }}>—</span>}
              </Field>
              <Field label="Website">
                {record?.website_url && /^https?:\/\//.test(record.website_url) ? (
                  <a href={record.website_url} target="_blank" rel="noreferrer noopener">
                    {record.website_url}
                  </a>
                ) : (
                  <span style={{ color: brand.sub }}>—</span>
                )}
              </Field>

              {(() => {
                const perk = record?.perks?.find((p) => p.active)
                return (
                  <Field label="Perk">
                    {perk ? (
                      <div>
                        <div style={{ fontWeight: 700 }}>{perk.title}</div>
                        {perk.details ? <div>{perk.details}</div> : null}
                        {perk.code ? (
                          <div style={{ color: brand.sub, fontSize: 12 }}>Code: {perk.code}</div>
                        ) : null}
                      </div>
                    ) : (
                      <span style={{ color: brand.sub }}>None on file</span>
                    )}
                    {switchesLoaded && !perksOn && perksRowMissing && (
                      <div
                        data-testid="listing-perks-unset-note"
                        style={{ color: brand.sub, fontSize: 12, marginTop: perk ? 6 : 4 }}
                      >
                        The Member perks switch isn&apos;t set up yet.
                      </div>
                    )}
                    {switchesLoaded && !perksOn && !perksRowMissing && (
                      <div
                        data-testid="listing-perks-off-note"
                        style={{ color: brand.sub, fontSize: 12, marginTop: perk ? 6 : 4 }}
                      >
                        Perks are off. Members don&apos;t see these.
                      </div>
                    )}
                  </Field>
                )
              })()}

              <Field label="Ask requests">{record?.accepts_requests ? 'Opted in' : 'Off'}</Field>
            </Card>
          </Col>

          {/* ── Right: Checks + Decision — only once the record has loaded.
              Refine's <Show isLoading> still renders children under a Spin
              overlay, so without this guard a still-loading (or errored) page
              flashes red "No class year" / "No" pills and an empty
              "…is ." sentence built from an undefined record. ────────── */}
          <Col xs={24} lg={10}>
            {record ? (
              <>
                <Card title={<span style={headStyle}>Checks</span>} style={{ marginBottom: 16 }}>
                  <Field label="Owner is a verified alum">
                    {record.owner?.class_year ? (
                      <Pill tone="green">Class of {record.owner.class_year}</Pill>
                    ) : (
                      <Pill tone="red">No class year</Pill>
                    )}
                  </Field>
                  <Field label="Ownership attested">
                    {record.is_owner_attested ? <Pill tone="green">Yes</Pill> : <Pill tone="red">No</Pill>}
                  </Field>
                  <Field label="Distributor or direct sales">
                    {record.is_mlm ? (
                      <Pill tone="jaguar">Yes — labeled, not spotlight-eligible</Pill>
                    ) : (
                      <Pill tone="neutral">No</Pill>
                    )}
                  </Field>
                  <Field label="Photo moderation">
                    <Pill tone={moderationTone(record.moderation_status)}>
                      {moderationLabel(record.moderation_status)}
                    </Pill>
                  </Field>
                  <Field label="Decision note on file">
                    {record.decision_note || <span style={{ color: brand.sub }}>—</span>}
                  </Field>
                </Card>

                <Card title={<span style={headStyle}>Decision</span>}>
                  {actions.length === 0 ? (
                    <p style={{ fontFamily: brand.fSans, fontSize: 13, color: brand.sub, margin: 0 }}>
                      No decision needed for a listing that is {statusLabel(record.status)}.
                    </p>
                  ) : (
                    <Space wrap>
                      {actions.map((action) => (
                        <Button
                          key={action}
                          type={action === 'approve' ? 'primary' : undefined}
                          danger={action === 'reject' || action === 'remove'}
                          data-testid={`business-${action.replace('_', '-')}`}
                          disabled={busy !== null}
                          loading={busy === action}
                          onClick={() => (action === 'approve' ? confirmApprove() : openNoteModal(action))}
                        >
                          {ACTION_BUTTON_LABEL[action]}
                        </Button>
                      ))}
                    </Space>
                  )}
                </Card>

                <p style={{ fontFamily: brand.fSans, fontSize: 11.5, color: brand.sub, marginTop: 14 }}>
                  Decisions run through a server function that also notifies the owner and writes the
                  audit trail. Listings can&apos;t be edited from here.
                </p>
              </>
            ) : null}
          </Col>
        </Row>

        {/* ── Vouches (Task P4) — moderate any vouch on this listing without
            waiting for a report. Rendered full width BELOW the Listing |
            Checks+Decision row above (not inside either column), capped by
            the outer 1200px div, since it's a table needing room, not a
            form. */}
        {record ? (
          <>
            {switchesLoaded && !vouchesOn && vouchesRowMissing && (
              <div
                data-testid="listing-vouches-unset-note"
                style={{ color: brand.sub, fontSize: 12, marginBottom: 8 }}
              >
                The Vouches switch isn&apos;t set up yet.
              </div>
            )}
            {switchesLoaded && !vouchesOn && !vouchesRowMissing && (
              <div data-testid="listing-vouches-off-note" style={{ color: brand.sub, fontSize: 12, marginBottom: 8 }}>
                Vouches are off. Members don&apos;t see these.
              </div>
            )}
            <VouchesCard businessId={record.id} />
          </>
        ) : null}
      </div>

      {/* ── Note modal (Request changes / Reject / Remove) ─────────── */}
      <Modal
        title={noteModal ? NOTE_MODAL_TITLE[noteModal] : ''}
        open={noteModal !== null}
        onCancel={closeNoteModal}
        okText={noteModal ? NOTE_MODAL_OK_TEXT[noteModal] : 'OK'}
        okButtonProps={{
          danger: noteModal === 'reject' || noteModal === 'remove',
          disabled: trimmedNote.length === 0 || busy !== null,
          loading: noteModal !== null && busy === noteModal,
        }}
        onOk={() => noteModal && decide(noteModal, trimmedNote)}
        destroyOnHidden
      >
        <div style={labelStyle}>Note to the owner (required)</div>
        <Input.TextArea
          data-testid="business-decision-note"
          rows={4}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Modal>
    </Show>
  )
}
